"use client";

import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";

import {
  ICLOSED_WIDGET_SRC,
  isIClosedCallScheduledMessage,
  isIClosedMessage,
} from "@/utils/iclosed";

const ICLOSED_SCRIPT_ID = "iclosed-widget";
/**
 * Treat the scheduler as ready even if iClosed never posts a message (e.g. the
 * widget script is blocked), so the open-in-new-tab fallback still surfaces.
 */
const ICLOSED_READY_TIMEOUT_MS = 8000;

interface IClosedInlineWidgetProps {
  /** Scheduler URL — typically built with `buildIClosedSchedulerUrl`. */
  url: string;
  /** Accessible title for the embedded scheduler iframe. */
  title: string;
  /** Fired once when iClosed reports a booked call. */
  onBooked?: () => void;
  /**
   * When provided, renders a fallback link below the embed that opens the
   * scheduler in a new tab (resilience against the widget script being blocked).
   */
  fallbackLabel?: string;
  className?: string;
}

/** Opaque overlay over the host until the scheduler boots, so iClosed's own
 *  in-iframe skeleton never shows through. */
const IClosedLoadingOverlay: React.FC = () => (
  <div className="iclosed-loading-overlay absolute inset-0 z-10 flex items-center justify-center bg-white">
    <span
      aria-hidden="true"
      className="h-8 w-8 animate-spin rounded-full border-2 border-gail-primary/25 border-t-gail-primary"
    />
    <span className="sr-only">Loading scheduler</span>
  </div>
);

/**
 * Renders the iClosed inline scheduler. The widget script (loaded once) scans
 * the DOM for `.iclosed-widget` elements and injects the scheduler iframe using
 * the element's `data-url`, so the `url` is fixed at mount. Centralizes the
 * third-party embed so booking flows only deal with the prefilled URL.
 */
export const IClosedInlineWidget: React.FC<IClosedInlineWidgetProps> = ({
  url,
  title,
  onBooked,
  fallbackLabel,
  className,
}) => {
  // Flips when the scheduler app posts its first `iclosed.*` message (i.e. it
  // has booted and is rendering), or after a timeout as a safety net. Controls
  // when our bridging skeleton is dropped and the fallback link appears —
  // showing the link mid-load had it popping in under the skeleton and then
  // jumping when iClosed resized the widget.
  const [isReady, setIsReady] = useState(false);

  // iClosed injects its iframe from the scheduler URL's origin, so every
  // legitimate `message` it posts comes from there. Validating `event.origin`
  // against it stops any other script/iframe on the page from spoofing
  // `iclosed.*` events (e.g. faking a booking conversion). When the URL can't
  // be parsed this is `null`, which never equals an origin string — every
  // message is rejected and readiness comes only from the timeout safety net.
  const expectedOrigin = useMemo(() => {
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }, [url]);

  useEffect(() => {
    if (document.getElementById(ICLOSED_SCRIPT_ID)) {
      return;
    }

    // No Subresource Integrity (SRI) hash: widget.js is an iClosed-managed loader
    // that they version/rotate without notice, so a pinned integrity hash would
    // break demo booking on every vendor redeploy. It's served over HTTPS from
    // the trusted iClosed origin, which is the accepted risk here.
    const script = document.createElement("script"); // NOSONAR S5725 - SRI not viable for iClosed's rotating loader (see note above)
    script.id = ICLOSED_SCRIPT_ID;
    script.src = ICLOSED_WIDGET_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) {
        return;
      }
      if (isIClosedMessage(event.data)) {
        setIsReady(true);
      }
    };

    globalThis.addEventListener("message", handleMessage);
    const timeout = globalThis.setTimeout(
      () => setIsReady(true),
      ICLOSED_READY_TIMEOUT_MS,
    );

    return () => {
      globalThis.removeEventListener("message", handleMessage);
      globalThis.clearTimeout(timeout);
    };
  }, [expectedOrigin]);

  useEffect(() => {
    if (!onBooked) {
      return;
    }

    let booked = false;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) {
        return;
      }
      if (booked || !isIClosedCallScheduledMessage(event.data)) {
        return;
      }
      booked = true;
      onBooked();
    };

    globalThis.addEventListener("message", handleMessage);
    return () => globalThis.removeEventListener("message", handleMessage);
  }, [onBooked, expectedOrigin]);

  return (
    <div className="flex w-full flex-col items-center gap-3">
      {/* relative z-0 scopes the overlay's z-10 here; min-height reserves space
          until iClosed sets the height via its `iclosed.widget_height` message. */}
      <div
        className={clsx(
          "iclosed-widget relative z-0 min-h-[480px] w-full",
          className,
        )}
        data-url={url}
        title={title}
      >
        {!isReady && <IClosedLoadingOverlay />}
      </div>
      {fallbackLabel && isReady && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-monument text-sm text-gail-primary underline underline-offset-2 transition-colors hover:text-gail-primary-hover"
        >
          {fallbackLabel}
        </a>
      )}
    </div>
  );
};
